<template>
  <div>
    <b-alert
      v-if="disabled"
      variant="warning"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ $t('A cobrança por KM não usa faixas de taxa. Limpe as faixas para salvar (I11).') }}
      </div>
    </b-alert>

    <FormulateInput
      v-model="internalTiers"
      :name="name"
      type="group"
      error-behavior="live"
      :repeatable="!disabled"
      :add-label="$t('Adicionar faixa de taxa')"
      :minimum="0"
      :disabled="disabled"
      :label="label"
    >
      <template #default="{ index }">
        <!-- O grupo Formulate re-renderiza o slot com o novo índice ANTES de o set() (splice)
             propagar para `tiers`, então internalTiers[index] fica indefinido por um frame ao
             adicionar/remover. O v-if evita o acesso a undefined (crash) nesse instante. -->
        <b-row v-if="internalTiers[index]">
          <b-col md="4">
            <FormulateInput
              :id="`${name}-from-${index}`"
              v-model="internalTiers[index].productsValueFrom"
              type="text-number"
              filter="currency"
              currency="R$"
              :precision="2"
              name="productsValueFrom"
              class="required"
              validation="required|min:0"
              :disabled="disabled"
              :label="$t('A partir de')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              :id="`${name}-value-${index}`"
              v-model="internalTiers[index].value"
              type="text-number"
              filter="currency"
              currency="R$"
              :precision="2"
              name="value"
              class="required"
              validation="required|min:0"
              :disabled="disabled || isFreeShipping(index)"
              :label="$t('Valor')"
            />
          </b-col>
          <b-col
            md="4"
            class="d-flex align-items-center"
          >
            <b-form-checkbox
              :id="`${name}-free-${index}`"
              switch
              :checked="isFreeShipping(index)"
              :disabled="disabled"
              @change="checked => toggleFreeShipping(index, checked)"
            >
              {{ $t('Frete grátis nesta faixa') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
      </template>
    </FormulateInput>
  </div>
</template>

<script>
import { BRow, BCol, BAlert, BFormCheckbox } from 'bootstrap-vue'

/**
 * Editor de faixas de taxa de uma lista — as faixas padrão da config ou as de um dia.
 *
 * Cada faixa guarda o limiar ("A partir de") e o valor em reais. O limite superior ("Até") é
 * derivado pelo backend (o "a partir de" da próxima faixa) e NÃO é exibido: seria somente leitura
 * e só confundiria o usuário (D2).
 *
 * O switch "frete grátis" é um atalho de preenchimento (D4): ligar grava `value = 0` e
 * desabilita o campo de valor; desligar reabilita. Não existe campo `isFreeShipping` no payload.
 *
 * Quando `disabled` (o tipo de cobrança efetivo é por KM), a edição é bloqueada com aviso (I11).
 */
export default {
  components: {
    BRow,
    BCol,
    BAlert,
    BFormCheckbox,
  },

  props: {
    // Lista de faixas (mesma referência do estado Vuex; mutada em lugar pelo componente).
    tiers: {
      type: Array,
      default: () => [],
    },
    name: {
      type: String,
      default: 'tiers',
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Guarda o valor anterior ao ligar "frete grátis", para reabilitar ao desligar.
      previousValues: {},
    }
  },

  computed: {
    // Bind bidirecional que preserva a mesma referência de `tiers` (estado Vuex): adição e
    // remoção pelo grupo repetível são refletidas com splice; edições de campo mutam os itens.
    internalTiers: {
      get() {
        return this.tiers
      },
      set(value) {
        this.tiers.splice(0, this.tiers.length, ...value)
      },
    },
  },

  methods: {
    isFreeShipping(index) {
      const tier = this.tiers[index]
      // Só é "frete grátis" quando o valor é explicitamente 0. Valor vazio (null/''/undefined)
      // NÃO é grátis — assim, ao desligar o switch, o campo de valor volta a ficar habilitado.
      if (!tier || tier.value === null || tier.value === undefined || tier.value === '') {
        return false
      }
      return Number(tier.value) === 0
    },

    toggleFreeShipping(index, checked) {
      const tier = this.tiers[index]
      if (!tier) return
      if (checked) {
        this.previousValues[index] = tier.value
        this.$set(tier, 'value', 0)
      } else {
        const previous = this.previousValues[index]
        this.$set(tier, 'value', previous && previous !== 0 ? previous : null)
      }
    },
  },
}
</script>

<style></style>
